import { localize, translationKey } from '../i18n';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import PageHeader from './PageHeader';
import { draftPrimaryButton, draftSecondaryButton, RegistrationDraftButton as Button } from './RegistrationDraftChoice';
import { createMedicationsBulk, getDeviceUuid, getRegistrationResult } from '../api';
import { readRegistrationDraft, registrationOwner, resolveRegistrationRequest } from '../utils/registrationDraft';
import useNavigate from '../hooks/useSafeNavigate';
import useBackHandler from '../hooks/useBackHandler';
import { clearMedicationCaches } from '../utils/localCache';
import { syncWidget } from '../utils/widgetSync';
import { createRegistrationJourney } from '../utils/productJourneys';
import BottomSheet from './BottomSheet';
import WebviewMessenger from '../utils/WebviewMessenger';

export default function RegistrationResultRecovery({ draft }) {
    const navigate = useNavigate();
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState(translationKey('등록 결과를 확인하고 있어요'));
    const [checked, setChecked] = useState(false);
    const [pastConfirmed, setPastConfirmed] = useState(false);
    const [discardOpen, setDiscardOpen] = useState(false);
    const running = useRef(false);
    const mounted = useRef(true);
    const journey = useRef(createRegistrationJourney(draft.source, draft.flowId));
    const current = () => {
        const stored = readRegistrationDraft();
        return stored?.id === draft.id && stored.status === 'pending' && registrationOwner()?.context === draft.context ? stored : null;
    };
    const finish = useCallback((fromLookup = false) => {
        if (!mounted.current || !current()) return;
        resolveRegistrationRequest(current(), true);
        if (fromLookup) {
            // A GET receipt does not run the successful POST interceptor.
            try { WebviewMessenger.send({ type: 'REFRESH_MEDICATION_ALARMS' }); } catch {}
            window.dispatchEvent(new Event('medications-updated'));
        }
        clearMedicationCaches(); syncWidget(getDeviceUuid());
        const prn = draft.path.includes('AS_NEEDED');
        navigate(prn ? '/prn' : '/', { replace: true, state: { refreshHome: Date.now(), toast: localize("약 등록 완료를 확인했어요") } });
    }, [draft.id, draft.context, draft.path, navigate]);
    const lookup = useCallback(async () => {
        if (running.current || !current()) return;
        running.current = true; setBusy(true); setMessage(translationKey("등록 결과를 확인하고 있어요"));
        try {
            const result = await getRegistrationResult(draft.uuid, draft.requestId, draft.context);
            if (result.status === 'saved') finish(true);
            else if (mounted.current) { setChecked(true); setMessage(translationKey("아직 완료된 결과를 확인하지 못했어요. 이미 등록됐다면 아래 재시도는 결과만 확인해요.")); }
        } catch { if (mounted.current) { setChecked(true); setMessage(translationKey("연결을 확인한 뒤 다시 확인해주세요. 이미 저장됐을 수 있어요.")); } }
        finally { running.current = false; if (mounted.current) setBusy(false); }
    }, [draft.uuid, draft.requestId, draft.context, finish]);
    useEffect(() => {
        mounted.current = true; journey.current.mount(); lookup();
        return () => { mounted.current = false; journey.current.unmount(); };
    }, [lookup]);
    useBackHandler(busy, () => {});
    const request = current()?.request;
    const today = new Date();
    const day = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const oldDates = request?.some(m => m.startDate < day || (m.endDate && m.endDate < day));
    const retry = async () => {
        const frozen = current();
        if (!frozen?.request || running.current || (oldDates && !pastConfirmed)) return;
        running.current = true; setBusy(true);
        try {
            await createMedicationsBulk(draft.uuid, frozen.request, { requestId: draft.requestId, expectedContext: draft.context });
            finish();
        } catch { if (mounted.current) setMessage(translationKey("등록 결과를 확인하지 못했어요. 작성 내용은 그대로 보관했어요. 다시 확인해주세요.")); }
        finally { running.current = false; if (mounted.current) setBusy(false); }
    };
    return <Box sx={{ bgcolor: '#fff', minHeight: '100vh', pb: 3 }}>
        <PageHeader title={localize("약 등록 결과 확인")} back={() => { if (!busy) navigate('/'); }} />
        <Box sx={{ px: 2.5, pt: 3 }}>
            <Typography component="h1" sx={{ fontSize: '1.35rem', fontWeight: 800 }}>{localize("약 등록 결과를 확인해주세요")}</Typography>
            <Typography role="status" sx={{ color: '#666', my: 2, lineHeight: 1.6 }}>{localize(message)}</Typography>
            {request && <Box sx={{ bgcolor: '#fafafa', borderRadius: 3, p: 2, mb: 2 }}>
                {request.map((m, i) => <Box key={i} sx={{ mb: 1.5 }}>
                    <Typography sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>{m.name}</Typography>
                    <Typography sx={{ color: '#666', fontSize: '0.95rem' }}>{m.startDate} ~ {m.endDate || localize("계속 복용")} · {m.times.join(', ') || localize("필요할 때")}</Typography>
                </Box>)}
            </Box>}
            <Box sx={{ display: 'grid', gap: 1 }}>
                <Button fullWidth variant="contained" sx={draftPrimaryButton} onClick={lookup} disabled={busy}>{busy ? localize("확인 중이에요") : localize("등록 결과 다시 확인하기")}</Button>
                {checked && request && (oldDates && !pastConfirmed ? <>
                    <Typography sx={{ color: '#666', lineHeight: 1.6 }}>{localize("시작일이 지났어요. 재시도하면 위의 원래 날짜와 설정으로 등록돼요.")}</Typography>
                    <Button fullWidth variant="contained" sx={draftSecondaryButton} onClick={() => setPastConfirmed(true)}>{localize("원래 날짜와 설정을 확인했어요")}</Button>
                </> : <Button fullWidth variant="contained" sx={draftSecondaryButton} onClick={retry} disabled={busy}>{localize("같은 내용으로 다시 등록하기")}</Button>)}
                {!request && <Typography sx={{ color: '#666', lineHeight: 1.6 }}>{localize("입력 내용의 보관 기간이 지났어요. 약 목록에서 등록 여부를 먼저 확인해주세요.")}</Typography>}
                <Button fullWidth variant="contained" sx={draftSecondaryButton} onClick={() => navigate('/medications')} disabled={busy}>{localize("약 목록 확인하기")}</Button>
                {!request && checked && <Button fullWidth variant="contained" sx={draftSecondaryButton} onClick={() => setDiscardOpen(true)} disabled={busy}>{localize("목록을 확인했어요 · 안내 지우기")}</Button>}
            </Box>
        </Box>
        <BottomSheet open={discardOpen} onClose={() => setDiscardOpen(false)} ariaLabelledBy="registration-result-discard-title">
            <Typography id="registration-result-discard-title" component="h2" sx={{ fontWeight: 800, fontSize: '1.2rem', mb: 1 }}>{localize("결과 확인 안내를 지울까요?")}</Typography>
            <Typography sx={{ color: '#666', lineHeight: 1.6, mb: 2 }}>{localize("약 목록을 먼저 확인해주세요. 안내를 지워도 이전 요청이 저장되지 않았다는 뜻은 아니에요.")}</Typography>
            <Box sx={{ display: 'grid', gap: 1 }}>
                <Button variant="contained" sx={draftSecondaryButton} onClick={() => setDiscardOpen(false)}>{localize("돌아가기")}</Button>
                <Button variant="contained" sx={draftPrimaryButton} onClick={() => { if (current()) resolveRegistrationRequest(current(), true); navigate('/', { replace: true }); }}>{localize("안내 지우기")}</Button>
            </Box>
        </BottomSheet>
    </Box>;
}
